import * as RadixDialog from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';
import { cn } from './cn.js';

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Visually hidden, but required by Radix for screen-reader announcement. */
  title: string;
  children: ReactNode;
  className?: string;
}

/**
 * A modal shell over Radix's dialog primitive: focus trapping, Escape-to-close
 * and outside-click dismissal come from Radix, styling and positioning from
 * our own tokens. Used for the command palette, and for any future editor
 * dialogs that need the same behaviour.
 */
export function Dialog({ open, onOpenChange, title, children, className }: DialogProps) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        {/* Dockview's drag overlays and resize sashes intentionally use
            z-indices up to 9999. Application modals must sit above that
            workspace chrome or sashes visibly cut through the dialog and
            intercept pointer events. */}
        <RadixDialog.Overlay className="fixed inset-0 z-[10000] bg-black/50" />
        <RadixDialog.Content
          className={cn(
            'fixed left-1/2 top-[15%] z-[10001] w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 overflow-hidden rounded-panel border border-border bg-surface-raised text-text shadow-2xl',
            className,
          )}
        >
          <RadixDialog.Title className="sr-only">{title}</RadixDialog.Title>
          {children}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
